import { useEffect, useState, Fragment } from 'react';
import api, { formatINR } from '@/lib/api';
import { toast } from 'sonner';
import { PencilSimple, X, Prohibit } from '@phosphor-icons/react';
import Invoice from '@/components/shop/Invoice';
import { ADMIN } from '@/constants/testIds';

const STATUSES = ['placed', 'confirmed', 'packed', 'out_for_delivery', 'delivered', 'cancelled', 'rejected'];

const STATUS_BADGE = {
  placed: 'bg-cream-200 text-stone-800',
  confirmed: 'bg-blue-100 text-blue-800',
  packed: 'bg-violet-100 text-violet-800',
  out_for_delivery: 'bg-amber-100 text-amber-800',
  delivered: 'bg-olive-100 text-olive-800',
  cancelled: 'bg-red-100 text-red-700',
  rejected: 'bg-rose-200 text-rose-900',
};

const REJECTABLE_STATUSES = ['placed', 'confirmed', 'packed'];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  // Revise modal
  const [reviseOrder, setReviseOrder] = useState(null);
  const [reviseItems, setReviseItems] = useState([]); // [{product_id, name, price, quantity, unit, image_url}]
  const [reviseDiscount, setReviseDiscount] = useState(0);
  const [reviseNote, setReviseNote] = useState('');
  const [reviseBusy, setReviseBusy] = useState(false);

  // Reject modal
  const [rejectOrder, setRejectOrder] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectBusy, setRejectBusy] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    if (filter) params.status = filter;
    api.get('/orders/admin/all', { params }).then((r) => setOrders(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const updateStatus = async (orderId, status) => {
    try {
      await api.put(`/orders/admin/${orderId}/status`, { status });
      toast.success('Status updated');
      setOrders((prev) => prev.map((o) => o.id === orderId ? { ...o, status } : o));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
  };

  const openRevise = (o) => {
    const base = o.revised_items || o.items || [];
    setReviseOrder(o);
    setReviseItems(base.map((it) => ({ ...it })));
    setReviseDiscount(o.revised_discount != null ? o.revised_discount : (o.discount || 0));
    setReviseNote(o.revised_note || '');
  };

  const closeRevise = () => {
    setReviseOrder(null);
    setReviseItems([]);
    setReviseDiscount(0);
    setReviseNote('');
  };

  const updateReviseQty = (pid, qty) => {
    const q = Math.max(0, parseInt(qty, 10) || 0);
    setReviseItems((prev) => prev.map((it) => it.product_id === pid ? { ...it, quantity: q } : it));
  };

  const reviseSubtotal = reviseItems.reduce((s, it) => s + (Number(it.price) * Number(it.quantity || 0)), 0);

  const saveRevise = async () => {
    const payloadItems = reviseItems.filter((it) => Number(it.quantity) > 0).map((it) => ({ product_id: it.product_id, quantity: Number(it.quantity) }));
    if (payloadItems.length === 0) { toast.error('At least one item with quantity > 0 required'); return; }
    setReviseBusy(true);
    try {
      const { data } = await api.put(`/orders/admin/${reviseOrder.id}/revise`, {
        items: payloadItems,
        discount: Number(reviseDiscount) || 0,
        note: reviseNote || '',
      });
      setOrders((prev) => prev.map((o) => o.id === data.id ? data : o));
      toast.success('Order revised');
      closeRevise();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revise failed');
    } finally {
      setReviseBusy(false);
    }
  };

  const openReject = (o) => { setRejectOrder(o); setRejectReason(''); };
  const closeReject = () => { setRejectOrder(null); setRejectReason(''); };
  const saveReject = async () => {
    const reason = rejectReason.trim();
    if (!reason) { toast.error('Rejection reason is required'); return; }
    setRejectBusy(true);
    try {
      const { data } = await api.put(`/orders/admin/${rejectOrder.id}/reject`, { reason });
      setOrders((prev) => prev.map((o) => o.id === data.id ? data : o));
      toast.success('Order rejected · hidden from customer');
      closeReject();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    } finally {
      setRejectBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-3xl text-stone-900">Orders</h1>
        <div className="flex gap-1.5 overflow-x-auto scrollbar-hide">
          <button onClick={() => setFilter('')} className={`shrink-0 px-3 h-8 rounded-full text-xs font-medium ${!filter ? 'bg-olive-800 text-cream-100' : 'bg-white border border-stone-200 text-stone-700'}`}>All</button>
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`shrink-0 px-3 h-8 rounded-full text-xs font-medium ${filter === s ? 'bg-olive-800 text-cream-100' : 'bg-white border border-stone-200 text-stone-700'}`}>{s.replace(/_/g, ' ')}</button>
          ))}
        </div>
      </div>

      {loading ? <p className="text-sm text-stone-500">Loading…</p> : orders.length === 0 ? (
        <p className="text-sm text-stone-500">No orders.</p>
      ) : (
        <div className="bg-white border border-stone-200 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-cream-100">
              <tr className="text-left text-stone-500 text-xs uppercase tracking-wider">
                <th className="py-3 px-4">Order</th><th className="px-3">Customer</th><th className="px-3">Items</th><th className="px-3">Total</th><th className="px-3">Status</th><th className="px-3">Date</th><th className="px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const isRevised = o.revised_total != null;
                const total = isRevised ? o.revised_total : o.total;
                const canRevise = !['delivered', 'cancelled', 'rejected'].includes(o.status);
                const canReject = REJECTABLE_STATUSES.includes(o.status);
                return (
                <Fragment key={o.id}>
                <tr className="border-t border-stone-100 hover:bg-cream-100/40 cursor-pointer" onClick={() => setOpen(open === o.id ? null : o.id)}>
                  <td className="py-2.5 px-4 font-medium text-stone-900">
                    {o.order_no}
                    {isRevised && <span data-testid={ADMIN.orderRevisedBadge(o.id)} className="ml-2 inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Revised</span>}
                  </td>
                  <td className="px-3">{o.customer_name}<br/><span className="text-xs text-stone-500">{o.customer_phone}</span></td>
                  <td className="px-3 text-stone-600">{(isRevised ? o.revised_items : o.items)?.length || 0}</td>
                  <td className="px-3 font-medium">
                    {formatINR(total)}
                    {isRevised && <div className="text-[10px] text-stone-400 line-through">{formatINR(o.total)}</div>}
                  </td>
                  <td className="px-3">
                    <select
                      data-testid={ADMIN.orderStatusSelect(o.id)}
                      onClick={(e) => e.stopPropagation()}
                      value={o.status}
                      onChange={(e) => updateStatus(o.id, e.target.value)}
                      className={`h-8 px-2 rounded-lg border border-stone-200 text-xs ${STATUS_BADGE[o.status] || 'bg-white'}`}
                    >
                      {STATUSES.filter((s) => s !== 'rejected' || o.status === 'rejected').map((s) => (
                        <option key={s} value={s} disabled={s === 'rejected' && o.status !== 'rejected'}>{s.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 text-stone-500 text-xs">{new Date(o.created_at).toLocaleString('en-IN')}</td>
                  <td className="px-3 text-right">
                    <button
                      type="button"
                      data-testid={ADMIN.orderReviseBtn(o.id)}
                      onClick={(e) => { e.stopPropagation(); if (canRevise) openRevise(o); }}
                      disabled={!canRevise}
                      title={canRevise ? 'Revise order' : 'Cannot revise delivered / cancelled / rejected'}
                      className="w-8 h-8 rounded-lg hover:bg-stone-100 inline-grid place-items-center text-stone-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <PencilSimple size={16}/>
                    </button>
                    <button
                      type="button"
                      data-testid={`admin-order-reject-btn-${o.id}`}
                      onClick={(e) => { e.stopPropagation(); if (canReject) openReject(o); }}
                      disabled={!canReject}
                      title={canReject ? 'Reject order (before dispatch)' : 'Cannot reject after dispatch'}
                      className="ml-1 w-8 h-8 rounded-lg hover:bg-rose-50 inline-grid place-items-center text-rose-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Prohibit size={16}/>
                    </button>
                  </td>
                </tr>
                {open === o.id && (
                  <tr className="border-t border-stone-100 bg-cream-100/40">
                    <td colSpan={7} className="px-4 py-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs uppercase tracking-wider text-stone-500">Delivery</p>
                          <p className="text-sm mt-1">{o.address?.line1}{o.address?.line2 ? `, ${o.address.line2}` : ''}, {o.address?.city}, {o.address?.state} - {o.address?.pincode}</p>
                          {o.address?.landmark && <p className="text-xs text-stone-500">Landmark: {o.address.landmark}</p>}
                          {o.notes && <p className="text-xs text-stone-500 mt-2">Notes: {o.notes}</p>}
                          {o.status === 'cancelled' && o.cancel_reason && (
                            <p className="text-xs text-red-700 mt-2">Cancelled — {o.cancel_reason} ({o.cancelled_by || 'admin'})</p>
                          )}
                          {o.status === 'rejected' && o.rejected_reason && (
                            <p data-testid={`admin-order-rejected-reason-${o.id}`} className="text-xs text-rose-800 mt-2 bg-rose-50 border border-rose-200 rounded-lg px-2 py-1.5">
                              <b>Rejected (admin-only):</b> {o.rejected_reason}
                              {o.rejected_at && <span className="block text-[10px] text-rose-600 mt-0.5">{new Date(o.rejected_at).toLocaleString('en-IN')}</span>}
                            </p>
                          )}
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wider text-stone-500">Items {isRevised && <span className="text-amber-700 normal-case">· revised</span>}</p>
                          <ul className="text-sm mt-1 space-y-1">
                            {(isRevised ? o.revised_items : o.items)?.map((it) => (
                              <li key={it.product_id} className="flex justify-between"><span>{it.name} <span className="text-stone-500">x{it.quantity}</span></span><span>{formatINR(it.price * it.quantity)}</span></li>
                            ))}
                          </ul>
                          <div className="text-xs text-stone-600 mt-2 space-y-0.5">
                            <div>Subtotal: {formatINR(isRevised ? o.revised_subtotal : o.subtotal)}</div>
                            <div>Delivery: {formatINR(isRevised ? o.revised_delivery_charge : o.delivery_charge)} · Platform: {formatINR(isRevised ? o.revised_platform_fee : o.platform_fee || 0)}</div>
                            {Number(isRevised ? o.revised_discount : o.discount) > 0 && <div className="text-olive-700">Discount: -{formatINR(isRevised ? o.revised_discount : o.discount)}</div>}
                            <div className="font-semibold text-stone-800">Total: {formatINR(total)}</div>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 pt-3 border-t border-stone-100">
                        <Invoice order={o} variant="admin" />
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              )})}
            </tbody>
          </table>
        </div>
      )}

      {reviseOrder && (
        <div className="fixed inset-0 z-50 bg-stone-900/40 flex items-end sm:items-center justify-center p-4 overflow-y-auto">
          <div data-testid={ADMIN.orderReviseModal} className="bg-white rounded-3xl border border-stone-200 w-full max-w-xl p-5 sm:p-6 my-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading text-xl">Revise order</h2>
                <p className="text-xs text-stone-500">{reviseOrder.order_no} · {reviseOrder.customer_name}</p>
              </div>
              <button type="button" onClick={closeRevise} className="w-8 h-8 rounded-full hover:bg-stone-100 grid place-items-center"><X size={18}/></button>
            </div>

            <div className="space-y-2 max-h-72 overflow-y-auto">
              {reviseItems.map((it) => (
                <div key={it.product_id} className="flex items-center gap-3 text-sm border-b border-stone-100 pb-2">
                  <img src={it.image_url || '/placeholder.png'} alt={it.name} className="w-9 h-9 rounded-lg object-cover bg-stone-100"/>
                  <div className="flex-1 min-w-0">
                    <p className="text-stone-900 line-clamp-1">{it.name}</p>
                    <p className="text-xs text-stone-500">{formatINR(it.price)} · {it.unit}</p>
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    data-testid={ADMIN.orderReviseQtyInput(it.product_id)}
                    value={it.quantity}
                    onChange={(e) => updateReviseQty(it.product_id, e.target.value)}
                    className="w-20 h-9 px-2 rounded-lg bg-white border border-stone-200 text-sm text-center"
                  />
                  <p className="w-20 text-right font-medium">{formatINR(it.price * (it.quantity || 0))}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
              <div>
                <label className="text-xs font-medium text-stone-600">Discount (₹)</label>
                <input
                  data-testid={ADMIN.orderReviseDiscount}
                  type="number"
                  min="0"
                  step="0.01"
                  value={reviseDiscount}
                  onChange={(e) => setReviseDiscount(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg bg-white border border-stone-200 text-sm mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-600">Revised subtotal</label>
                <div className="h-10 px-3 rounded-lg bg-stone-50 border border-stone-200 text-sm mt-1 grid items-center font-medium">{formatINR(reviseSubtotal)}</div>
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-stone-600">Note (optional)</label>
                <input
                  data-testid={ADMIN.orderReviseNote}
                  value={reviseNote}
                  onChange={(e) => setReviseNote(e.target.value)}
                  placeholder="e.g. Replaced unavailable item with similar"
                  className="w-full h-10 px-3 rounded-lg bg-white border border-stone-200 text-sm mt-1"
                />
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button type="button" data-testid={ADMIN.orderReviseCancelBtn} onClick={closeRevise} className="h-10 px-5 rounded-full border border-stone-300 text-stone-700 text-sm">Cancel</button>
              <button type="button" data-testid={ADMIN.orderReviseSaveBtn} onClick={saveRevise} disabled={reviseBusy} className="h-10 px-6 rounded-full bg-terracotta-600 text-white font-semibold hover:bg-terracotta-700 text-sm disabled:bg-stone-300">{reviseBusy ? 'Saving…' : 'Save revision'}</button>
            </div>
          </div>
        </div>
      )}

      {rejectOrder && (
        <div className="fixed inset-0 z-50 bg-stone-900/40 flex items-end sm:items-center justify-center p-4 overflow-y-auto">
          <div data-testid="admin-order-reject-modal" className="bg-white rounded-3xl border border-stone-200 w-full max-w-md p-5 sm:p-6 my-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading text-xl text-rose-800">Reject order</h2>
                <p className="text-xs text-stone-500">{rejectOrder.order_no} · {rejectOrder.customer_name}</p>
              </div>
              <button type="button" onClick={closeReject} className="w-8 h-8 rounded-full hover:bg-stone-100 grid place-items-center"><X size={18}/></button>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-xs text-rose-900 mb-3">
              This order will stop processing immediately and disappear from the customer's active orders.
              Stock will be restored. The reason below is <b>visible to admins only</b>.
            </div>
            <label className="text-xs font-medium text-stone-600">Rejection reason (admin-only)</label>
            <textarea
              data-testid="admin-order-reject-reason"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              maxLength={500}
              placeholder="e.g. Stock mismatch, fraud flag, duplicate order, out of service area"
              className="w-full px-3 py-2 rounded-lg bg-white border border-stone-200 focus:border-rose-400 focus:outline-none text-sm mt-1"
            />
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={closeReject} className="h-10 px-5 rounded-full border border-stone-300 text-stone-700 text-sm">Cancel</button>
              <button
                type="button"
                data-testid="admin-order-reject-confirm"
                onClick={saveReject}
                disabled={rejectBusy || !rejectReason.trim()}
                className="h-10 px-6 rounded-full bg-rose-700 text-white font-semibold hover:bg-rose-800 text-sm disabled:bg-stone-300"
              >
                {rejectBusy ? 'Rejecting…' : 'Reject order'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
